import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  User,
  Users,
  FileText,
  Settings,
  CheckCircle,
  MessageSquare,
  Activity,
  Calendar,
  Clock,
  LayoutDashboard,
  Component,
  Share2
} from "lucide-react";
import { supabase } from '@/lib/supabaseClient';
import { formatDistanceToNow, format } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';
import { fetchManagementAccess, holderLabel } from '@/lib/managementAccess';

interface ActivityLog {
  id: number;
  project_id?: string;
  user_id?: string;
  organization_id?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  action_type: string;
  impersonated_by_user_id?: string;
  impersonated_by_email?: string;
  details?: {
    message_text?: string;
    old_text?: string;
    new_text?: string;
    comment_text?: string;
    old?: Record<string, any>;
    new?: Record<string, any>;
    field?: string;
    [key: string]: any;
  } | null;
  timestamp: string;
  user?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  project?: {
    id: string;
    project_name?: string;
    company_name?: string;
    first_name?: string;
    last_name?: string;
  };
  assigneeUser?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  oldAssigneeUser?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  newAssigneeUser?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
}

export const LogsTab: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActionType, setSelectedActionType] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('7d');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { toast } = useToast();


  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'user_created', label: 'User Created' },
    { value: 'user_updated', label: 'User Updated' },
    { value: 'user_deleted', label: 'User Deleted' },
    { value: 'user_restored', label: 'User Restored' },
    { value: 'user_role_changed', label: 'User Role Changed' },
    { value: 'team_created', label: 'Team Created' },
    { value: 'team_updated', label: 'Team Updated' },
    { value: 'team_deleted', label: 'Team Deleted' },
    { value: 'project_created', label: 'Project Created' },
    { value: 'project_updated', label: 'Project Updated' },
    { value: 'project_assigned', label: 'Project Assigned' },
    { value: 'project_unassigned', label: 'Project Unassigned' },
    { value: 'stage_changed', label: 'Stage Changed' },
    { value: 'document_uploaded', label: 'Document Uploaded' },
    { value: 'document_deleted', label: 'Document Deleted' },
    { value: 'file_downloaded', label: 'File Downloaded' },
    { value: 'note_added', label: 'Note Added' },
    { value: 'task_created', label: 'Task Created' },
    { value: 'task_updated', label: 'Task Updated' },
    { value: 'task_completed', label: 'Task Completed' },
    { value: 'task_assigned', label: 'Task Assigned' },
    { value: 'task_reassigned', label: 'Task Reassigned' },
    { value: 'task_status_changed', label: 'Task Status Changed' },
    { value: 'task_deleted', label: 'Task Deleted' },
    { value: 'team_member_added', label: 'Team Member Added' },
    { value: 'team_member_removed', label: 'Team Member Removed' },
    { value: 'checklist_item_updated', label: 'Checklist Updated' },
    { value: 'message_sent', label: 'Message Sent' },
    { value: 'message_edited', label: 'Message Edited' },
    { value: 'comment_added', label: 'Comment Added' },
    { value: 'comment_edited', label: 'Comment Edited' },
    { value: 'installer_created', label: 'Installer Created' },
    { value: 'installer_updated', label: 'Installer Updated' },
    { value: 'installer_deleted', label: 'Installer Deleted' },
    { value: 'login', label: 'User Login' },
    { value: 'logout', label: 'User Logout' },
    { value: 'password_changed', label: 'Password Changed' },
    { value: 'password_reset_requested', label: 'Password Reset Requested' },
    { value: 'password_reset_completed', label: 'Password Reset Completed' },
    { value: 'email_verified', label: 'Email Verified' },
    { value: 'account_locked', label: 'Account Locked' },
    { value: 'account_unlocked', label: 'Account Unlocked' },
    { value: 'failed_login_attempt', label: 'Failed Login Attempt' },
    { value: 'session_expired', label: 'Session Expired' },
    { value: 'search_performed', label: 'Search Performed' },
    { value: 'data_exported', label: 'Data Exported' },
    { value: 'bulk_operation', label: 'Bulk Operation' },
    { value: 'settings_updated', label: 'Settings Updated' },
    { value: 'impersonation_started', label: 'Impersonation Started' },
    { value: 'impersonation_ended', label: 'Impersonation Ended' },
    // Dashboard activity types (DASH-23.0)
    { value: 'dashboard_created', label: 'Dashboard Created' },
    { value: 'dashboard_updated', label: 'Dashboard Updated' },
    { value: 'dashboard_deleted', label: 'Dashboard Deleted' },
    { value: 'dashboard_shared', label: 'Dashboard Shared' },
    { value: 'dashboard_permissions_changed', label: 'Dashboard Permissions Changed' },
    { value: 'widget_added', label: 'Widget Added' },
    { value: 'widget_updated', label: 'Widget Updated' },
    { value: 'widget_removed', label: 'Widget Removed' },
    { value: 'widget_template_saved', label: 'Widget Template Saved' },
    { value: 'widget_template_shared', label: 'Widget Template Shared' }
  ];

  // State for storing users and projects for dropdowns
  const [users, setUsers] = useState<Array<{id: string, name: string}>>([]);
  const [projects, setProjects] = useState<Array<{id: string, name: string}>>([]);

  const fetchLogs = useCallback(async (reset = false) => {
    // Prevent multiple simultaneous fetches
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) {
        console.error('Error getting tenant ID:', tenantError);
        toast({
          title: "Error",
          description: "Failed to get tenant ID.",
          variant: "destructive",
        });
        return;
      }

      
      let query = supabase
        .from('activity_logs')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('timestamp', { ascending: false });

      // Apply filters
      if (selectedActionType !== 'all') {
        // Handle equivalent action types (CREATED/project_created, UPDATED/project_updated)
        if (selectedActionType === 'project_created') {
          query = query.in('action_type', ['project_created', 'CREATED']);
        } else if (selectedActionType === 'project_updated') {
          query = query.in('action_type', ['project_updated', 'UPDATED']);
        } else {
          query = query.eq('action_type', selectedActionType);
        }
      }

      if (selectedUser !== 'all') {
        query = query.eq('user_id', selectedUser);
      }

      if (selectedProject !== 'all') {
        query = query.eq('project_id', selectedProject);
      }

      if (searchQuery) {
        query = query.or(`details->>'message_text'.ilike.%${searchQuery}%,details->>'comment_text'.ilike.%${searchQuery}%`);
      }

      // Apply date range filter
      const now = new Date();
      let startDate = new Date();
      switch (dateRange) {
        case 'today':
          // Set to start of today (midnight)
          startDate.setHours(0, 0, 0, 0);
          break;
        case '1d':
          startDate.setDate(now.getDate() - 1);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        default:
          startDate = new Date(0); // All time
      }
      
      if (dateRange !== 'all') {
        query = query.gte('timestamp', startDate.toISOString());
      }

      // Apply pagination
      const pageSize = 50;
      const from = reset ? 0 : (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      query = query.range(from, to);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching logs:', error);
        toast({
          title: "Error",
          description: "Failed to fetch activity logs.",
          variant: "destructive",
        });
        return;
      }

      // Fetch user details for all user_ids in the logs
      const allUserIds = (data || []).map(log => log.user_id);
      // Also collect assignee_ids from task logs
      const assigneeIds = (data || [])
        .filter(log => log.action_type.startsWith('task_'))
        .flatMap(log => [
          log.details?.assignee_id,
          log.details?.old_assignee_id,
          log.details?.new_assignee_id
        ])
        .filter(Boolean);
      
      const userIds = [...new Set([...allUserIds, ...assigneeIds].filter(Boolean))];
      const userMap = new Map();
      
      
      if (userIds.length > 0) {
        
        
        // First try to get users from the public.users table
        //
        // ⚠️ SUB-3.14 (FR61, NFR5). NO CLIENT-SIDE TENANT FILTER HERE, ON PURPOSE.
        // An Agency Admin acting inside a client account acts as themselves, but
        // their `users.tenant_id` is the PARENT account, so `.eq('tenant_id',
        // tenantId)` dropped their row: the entry rendered as an 8-character uuid
        // fragment where a local user's rendered as a name, and fell back to
        // `get_auth_users_info` for an email at best. That is the "never
        // anonymously, never differently from a local user" FR61 forbids.
        //
        // Removing the filter widens nothing. `users_select` admits the caller's
        // own row, rows in their tenant, and rows for anyone holding a `user_roles`
        // row in their CURRENT account — exactly the people who can act in this
        // account, and therefore exactly the people who can appear in its history.
        // RLS decides, rather than a client filter that has to be kept in step with
        // it.
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name')
          .in('id', userIds);
        
        if (usersError) {
          console.error('Error fetching users:', usersError);
        }
        
        if (!usersError && usersData) {
          
          usersData.forEach(user => {
            userMap.set(user.id, user);
          });
        }
        
        // For any users not found in public.users, try to get basic info from auth.users
        const foundUserIds = new Set(usersData?.map(u => u.id) || []);
        const missingUserIds = userIds.filter(id => !foundUserIds.has(id));
        
        if (missingUserIds.length > 0) {
          
          
          // Try to get basic user info from auth.users using a function call
          try {
            const { data: authUsersData, error: authError } = await supabase
              .rpc('get_auth_users_info', { user_ids: missingUserIds });
            
            if (!authError && authUsersData) {
              
              authUsersData.forEach((user: any) => {
                userMap.set(user.id, {
                  id: user.id,
                  email: user.email,
                  first_name: user.raw_user_meta_data?.first_name || user.email?.split('@')[0],
                  last_name: user.raw_user_meta_data?.last_name || ''
                });
              });
            } else {
              
            }
          } catch (error) {
            
          }
        }
        
        
      }

      // Fetch project details for all project_ids in the logs
      const projectIds = [...new Set((data || []).map(log => log.project_id).filter(Boolean))];
      const projectMap = new Map();
      
      if (projectIds.length > 0) {
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('id, project_name, company_name, first_name, last_name')
          .in('id', projectIds)
          .eq('tenant_id', tenantId);
        
        if (!projectsError && projectsData) {
          projectsData.forEach(project => {
            projectMap.set(project.id, project);
          });
        }
      }

      // Map user and project data to logs
      const logsWithDetails = (data || []).map(log => {
        const user = log.user_id ? userMap.get(log.user_id) : undefined;
        const project = log.project_id ? projectMap.get(log.project_id) : undefined;
        
        // Add assignee user details for task logs
        const assigneeUser = log.details?.assignee_id ? userMap.get(log.details.assignee_id) : undefined;
        const oldAssigneeUser = log.details?.old_assignee_id ? userMap.get(log.details.old_assignee_id) : undefined;
        const newAssigneeUser = log.details?.new_assignee_id ? userMap.get(log.details.new_assignee_id) : undefined;
        
        return {
          ...log,
          user,
          project,
          assigneeUser,
          oldAssigneeUser,
          newAssigneeUser
        };
      });

      if (reset) {
        setLogs(logsWithDetails);
        setPage(1);
      } else {
        setLogs(prev => [...prev, ...logsWithDetails]);
      }

      setHasMore((data || []).length === pageSize);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch activity logs.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedActionType, selectedUser, selectedProject, searchQuery, dateRange, page, toast, loading]);

  useEffect(() => {
    fetchLogs(true);
  }, [selectedActionType, selectedUser, selectedProject, dateRange]);

  // Fetch users for dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) return;

      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, email')
        .eq('tenant_id', tenantId)
        .order('first_name');

      if (!error && data) {
        const userOptions = data.map(user => ({
          id: user.id,
          name: user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : user.email
        }));

        // SUB-3.14 (FR61). People who hold access to this account through an
        // outside organization are not members of it, so the tenant-filtered query
        // above cannot see them — yet their actions appear in the list below. A
        // client asking "what has that firm done in my account" needs them in the
        // filter, or the question is unanswerable from this screen. Returns
        // nothing for an account nobody outside can reach, so an ordinary account's
        // dropdown is unchanged.
        const access = await fetchManagementAccess();
        const listed = new Set(userOptions.map(u => u.id));
        for (const holder of access?.holders ?? []) {
          if (listed.has(holder.userId)) continue;
          userOptions.push({ id: holder.userId, name: holderLabel(holder) });
        }

        setUsers(userOptions);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Fetch projects for dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) return;

      const { data, error } = await supabase
        .from('projects')
        .select('id, project_name, company_name, first_name, last_name')
        .eq('tenant_id', tenantId)
        .order('project_name');

      if (!error && data) {
        const projectOptions = data.map(project => ({
          id: project.id,
          name: project.project_name || project.company_name ||
            (project.first_name && project.last_name ? `${project.first_name} ${project.last_name}` : project.id)
        }));
        setProjects(projectOptions);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  // Fetch users and projects on component mount
  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, [fetchUsers, fetchProjects]);

  const handleSearch = () => {
    fetchLogs(true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
      fetchLogs(false);
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'user_created':
      case 'user_updated':
      case 'user_deleted':
      case 'user_restored':
        return <User className="w-4 h-4" />;
      case 'team_created':
      case 'team_updated':
      case 'team_deleted':
      case 'team_member_added':
        return <Users className="w-4 h-4" />;
      case 'project_created':
      case 'CREATED':
      case 'project_updated':
      case 'UPDATED':
      case 'stage_changed':
        return <FileText className="w-4 h-4" />;
      case 'document_uploaded':
      case 'document_deleted':
        return <FileText className="w-4 h-4" />;
      case 'task_created':
      case 'task_updated':
      case 'task_completed':
      case 'task_assigned':
      case 'task_reassigned':
      case 'task_status_changed':
      case 'task_deleted':
        return <CheckCircle className="w-4 h-4" />;
      case 'message_sent':
      case 'message_edited':
        return <MessageSquare className="w-4 h-4" />;
      case 'comment_added':
      case 'comment_edited':
        return <MessageSquare className="w-4 h-4" />;
      case 'installer_created':
      case 'installer_updated':
      case 'installer_deleted':
        return <Users className="w-4 h-4" />;
      case 'login':
      case 'logout':
      case 'session_expired':
        return <Activity className="w-4 h-4" />;
      case 'password_changed':
      case 'password_reset_requested':
      case 'password_reset_completed':
      case 'email_verified':
        return <Settings className="w-4 h-4" />;
      case 'account_locked':
      case 'account_unlocked':
      case 'failed_login_attempt':
        return <User className="w-4 h-4" />;
      case 'user_role_changed':
        return <User className="w-4 h-4" />;
      case 'project_assigned':
      case 'project_unassigned':
        return <FileText className="w-4 h-4" />;
      case 'file_downloaded':
        return <Download className="w-4 h-4" />;
      case 'search_performed':
        return <Search className="w-4 h-4" />;
      case 'data_exported':
        return <Download className="w-4 h-4" />;
      case 'bulk_operation':
        return <Settings className="w-4 h-4" />;
      case 'settings_updated':
        return <Settings className="w-4 h-4" />;
      case 'impersonation_started':
      case 'impersonation_ended':
        return <User className="w-4 h-4" />;
      // Dashboard activity types (DASH-23.0)
      case 'dashboard_created':
      case 'dashboard_updated':
      case 'dashboard_deleted':
      case 'dashboard_permissions_changed':
        return <LayoutDashboard className="w-4 h-4" />;
      case 'widget_added':
      case 'widget_updated':
      case 'widget_removed':
        return <Component className="w-4 h-4" />;
      case 'dashboard_shared':
      case 'widget_template_saved':
      case 'widget_template_shared':
        return <Share2 className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (actionType: string) => {
    if (actionType === 'impersonation_started' || actionType === 'impersonation_ended') {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    }
    // Dashboard-specific colors (DASH-23.0)
    if (actionType.includes('shared')) {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    }
    if (actionType.includes('permissions')) {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    }
    if (actionType.includes('created') || actionType.includes('added')) {
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400';
    }
    if (actionType.includes('deleted') || actionType.includes('removed')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    }
    if (actionType.includes('updated') || actionType.includes('edited')) {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    }
    if (actionType.includes('completed')) {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    }
    return 'bg-muted text-muted-foreground';
  };

  const formatUserName = (user?: { first_name?: string; last_name?: string; email: string }, userId?: string) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else if (user?.email) {
      return user.email;
    } else if (userId) {
      return `${userId.substring(0, 8)}...`;
    }
    return 'Unknown User';
  };

  const formatLogMessage = (log: ActivityLog) => {
    let userName = 'System';
    
    if (log.user?.first_name && log.user?.last_name) {
      userName = `${log.user.first_name} ${log.user.last_name}`;
    } else if (log.user?.email) {
      userName = log.user.email;
    } else if (log.user_id) {
      // Show first 8 characters of user ID as fallback
      userName = `${log.user_id.substring(0, 8)}...`;
    }
    
    // Check if this action was performed during impersonation
    const impersonatedBy = log.details?.impersonated_by_email;
    const impersonationSuffix = impersonatedBy ? ` (impersonated by ${impersonatedBy})` : '';
    
    const projectName = log.project?.project_name || log.project?.company_name || 
      (log.project?.first_name && log.project?.last_name ? `${log.project.first_name} ${log.project.last_name}` : null) ||
      log.project_id || 'Unknown Project';

    switch (log.action_type) {
      case 'user_created':
        return `${userName} created a new user account`;
      case 'user_updated':
        return `${userName} updated user information`;
      case 'user_deleted':
        return `${userName} deleted a user account`;
      case 'user_restored':
        return `${userName} restored a deleted user account`;
      case 'team_created':
        return `${userName} created a new team`;
      case 'team_updated':
        return `${userName} updated team information`;
      case 'team_deleted':
        return `${userName} deleted a team`;
      case 'team_member_added':
        return `${userName} added a team member`;
      case 'team_member_removed':
        return `${userName} removed a team member`;
      case 'project_created':
      case 'CREATED':
        return `${userName} created project: ${projectName}`;
      case 'project_updated':
      case 'UPDATED':
        return `${userName} updated project: ${projectName}`;
      case 'stage_changed':
        return `${userName} changed stage for project: ${projectName}`;
      case 'document_uploaded':
        return `${userName} uploaded a document to project: ${projectName}`;
      case 'document_deleted':
        return `${userName} deleted a document from project: ${projectName}`;
      case 'task_created':
        const taskTitle = log.details?.title || 'Untitled Task';
        if (log.project_id) {
          return `${userName} created task "${taskTitle}" in project: ${projectName}`;
        } else {
          return `${userName} created personal task "${taskTitle}"`;
        }
      case 'task_updated':
        const updatedTaskTitle = log.details?.title || 'Untitled Task';
        if (log.project_id) {
          return `${userName} updated task "${updatedTaskTitle}" in project: ${projectName}`;
        } else {
          return `${userName} updated personal task "${updatedTaskTitle}"`;
        }
      case 'task_completed':
        const completedTaskTitle = log.details?.title || 'Untitled Task';
        if (log.project_id) {
          return `${userName} completed task "${completedTaskTitle}" in project: ${projectName}`;
        } else {
          return `${userName} completed personal task "${completedTaskTitle}"`;
        }
      case 'task_assigned':
        const assignedTaskTitle = log.details?.title || 'Untitled Task';
        const assigneeName = formatUserName(log.assigneeUser, log.details?.assignee_id);
        if (log.project_id) {
          return `${userName} assigned task "${assignedTaskTitle}" to ${assigneeName} in project: ${projectName}`;
        } else {
          return `${userName} assigned personal task "${assignedTaskTitle}" to ${assigneeName}`;
        }
      case 'task_reassigned':
        const reassignedTaskTitle = log.details?.title || 'Untitled Task';
        const oldAssigneeName = formatUserName(log.oldAssigneeUser, log.details?.old_assignee_id);
        const newAssigneeName = formatUserName(log.newAssigneeUser, log.details?.new_assignee_id);
        if (log.project_id) {
          return `${userName} reassigned task "${reassignedTaskTitle}" from ${oldAssigneeName} to ${newAssigneeName} in project: ${projectName}`;
        } else {
          return `${userName} reassigned personal task "${reassignedTaskTitle}" from ${oldAssigneeName} to ${newAssigneeName}`;
        }
      case 'task_status_changed':
        const statusTaskTitle = log.details?.title || 'Untitled Task';
        const oldStatus = log.details?.old_status || 'Unknown';
        const newStatus = log.details?.new_status || 'Unknown';
        if (log.project_id) {
          return `${userName} changed status of task "${statusTaskTitle}" from ${oldStatus} to ${newStatus} in project: ${projectName}`;
        } else {
          return `${userName} changed status of personal task "${statusTaskTitle}" from ${oldStatus} to ${newStatus}`;
        }
      case 'task_deleted':
        const deletedTaskTitle = log.details?.title || 'Untitled Task';
        if (log.project_id) {
          return `${userName} deleted task "${deletedTaskTitle}" from project: ${projectName}`;
        } else {
          return `${userName} deleted personal task "${deletedTaskTitle}"`;
        }
      case 'message_sent':
        return `${userName} sent a message in project: ${projectName}`;
      case 'comment_added':
        return `${userName} added a comment in project: ${projectName}`;
      case 'login':
        return `${userName} logged in`;
      case 'logout':
        return `${userName} logged out`;
      case 'password_changed':
        return `${userName} changed their password`;
      case 'user_role_changed':
        const oldRole = log.details?.old_role || 'Unknown';
        const newRole = log.details?.new_role || 'Unknown';
        return `${userName} changed user role from ${oldRole} to ${newRole}`;
      case 'project_assigned':
        const assignedUserId = log.details?.assigned_user_id;
        return `${userName} assigned user ${assignedUserId ? `(${assignedUserId.substring(0, 8)}...)` : ''} to project: ${projectName}`;
      case 'project_unassigned':
        const unassignedUserId = log.details?.unassigned_user_id;
        return `${userName} unassigned user ${unassignedUserId ? `(${unassignedUserId.substring(0, 8)}...)` : ''} from project: ${projectName}`;
      case 'file_downloaded':
        const fileName = log.details?.file_name || 'Unknown file';
        return `${userName} downloaded file: ${fileName}`;
      case 'password_reset_requested':
        return `${userName} requested a password reset`;
      case 'password_reset_completed':
        return `${userName} completed password reset`;
      case 'email_verified':
        return `${userName} verified their email address`;
      case 'account_locked':
        const lockReason = log.details?.reason || 'Unknown reason';
        return `${userName} account was locked: ${lockReason}`;
      case 'account_unlocked':
        return `${userName} account was unlocked`;
      case 'failed_login_attempt':
        const failedEmail = log.details?.email || 'Unknown email';
        const failReason = log.details?.reason || 'Invalid credentials';
        return `Failed login attempt for ${failedEmail}: ${failReason}`;
      case 'session_expired':
        return `${userName} session expired`;
      case 'search_performed':
        const searchTerm = log.details?.search_term || 'Unknown';
        const resultsCount = log.details?.results_count;
        return `${userName} searched for "${searchTerm}"${resultsCount ? ` (${resultsCount} results)` : ''}`;
      case 'data_exported':
        const exportType = log.details?.export_type || 'Unknown';
        const recordCount = log.details?.record_count;
        return `${userName} exported ${exportType} data${recordCount ? ` (${recordCount} records)` : ''}`;
      case 'bulk_operation':
        const operationType = log.details?.operation_type || 'Unknown';
        const affectedCount = log.details?.affected_count || 0;
        return `${userName} performed bulk ${operationType} operation (${affectedCount} items affected)`;
      case 'settings_updated':
        return `${userName} updated system settings`;
      case 'impersonation_started':
        const targetUserEmail = log.details?.target_user_email || 'Unknown User';
        const originalUserEmail = log.details?.original_user_email || userName;
        return `${originalUserEmail} started impersonating ${targetUserEmail}`;
      case 'impersonation_ended':
        const endTargetUserEmail = log.details?.target_user_email || 'Unknown User';
        const endOriginalUserEmail = log.details?.original_user_email || userName;
        return `${endOriginalUserEmail} stopped impersonating ${endTargetUserEmail}`;
      // Dashboard activity types (DASH-23.0)
      case 'dashboard_created':
        return `${userName} created dashboard: ${log.details?.name || 'Unnamed'}`;
      case 'dashboard_updated':
        return `${userName} updated dashboard: ${log.details?.name?.new || log.details?.name || 'Unnamed'}`;
      case 'dashboard_deleted':
        return `${userName} deleted dashboard: ${log.details?.name || 'Unnamed'}`;
      case 'dashboard_shared':
        return `${userName} shared dashboard to tenant: ${log.details?.target_tenant_name || 'Unknown'}`;
      case 'dashboard_permissions_changed':
        const permAction = log.details?.action || 'changed';
        return `${userName} ${permAction} dashboard permissions`;
      case 'widget_added':
        return `${userName} added widget: ${log.details?.widget_type_id || 'Unknown type'}`;
      case 'widget_updated':
        return `${userName} updated widget configuration`;
      case 'widget_removed':
        return `${userName} removed widget from dashboard`;
      case 'widget_template_saved':
        return `${userName} saved widget as template: ${log.details?.template_name || 'Unnamed'}`;
      case 'widget_template_shared':
        const targetCount = log.details?.target_tenant_ids?.length || 0;
        return `${userName} shared widget template to ${targetCount} tenant(s)`;
      default:
        // Handle various action type formats
        const actionType = log.action_type.toLowerCase();
        if (actionType === 'updated') {
          // Check if this is just a timestamp update
          const details = log.details;
          if (details?.old && details?.new && typeof details.old === 'object' && typeof details.new === 'object') {
            const oldData = details.old as Record<string, any>;
            const newData = details.new as Record<string, any>;
            const oldKeys = Object.keys(oldData);
            const newKeys = Object.keys(newData);
            const changedKeys = newKeys.filter(key => 
              JSON.stringify(oldData[key]) !== JSON.stringify(newData[key])
            );
            
            // If only updated_at changed, it's likely a system update
            if (changedKeys.length === 1 && changedKeys[0] === 'updated_at') {
              return `System updated project timestamp: ${projectName}`;
            }
          }
          return `${userName} updated project: ${projectName}`;
        }
        if (actionType === 'created') {
          return `${userName} created project: ${projectName}`;
        }
        return log.details?.message_text || `${userName} performed ${log.action_type.replace(/_/g, ' ')}`;
    }
  };
  
  const getLogMessageWithImpersonation = (log: ActivityLog) => {
    const baseMessage = formatLogMessage(log);
    const impersonatedByEmail = log.impersonated_by_email;
    
    // Don't add impersonation suffix to impersonation start/end logs themselves
    if (log.action_type === 'impersonation_started' || log.action_type === 'impersonation_ended') {
      return baseMessage;
    }
    
    return impersonatedByEmail ? `${baseMessage} (impersonated by ${impersonatedByEmail})` : baseMessage;
  };

  const exportLogs = async () => {
    try {
      const csvData = logs.map(log => ({
        Timestamp: format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss'),
        Action: log.action_type,
        User: log.user?.email || log.user_id || 'System',
        Project: log.project?.project_name || log.project?.company_name || 
          (log.project?.first_name && log.project?.last_name ? `${log.project.first_name} ${log.project.last_name}` : null) ||
          log.project_id || 'N/A',
        Entity: log.related_entity_type || 'N/A',
        Details: JSON.stringify(log.details || {})
      }));

      const csvContent = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_logs_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Activity logs exported successfully.",
      });
    } catch (error) {
      console.error('Error exporting logs:', error);
      toast({
        title: "Error",
        description: "Failed to export activity logs.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Activity Logs</h2>
          <p className="text-muted-foreground">
            Comprehensive view of all system activities and user actions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchLogs(true)}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportLogs}
            disabled={logs.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="h-10"
                />
                <Button className="h-10" onClick={handleSearch}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Action Type</label>
              <SearchableSelect
                options={actionTypes.map(type => ({ label: type.label, value: type.value }))}
                value={selectedActionType}
                onValueChange={setSelectedActionType}
                placeholder="Select action type"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">User</label>
              <SearchableSelect
                options={[
                  { label: 'All Users', value: 'all' },
                  ...users.map(user => ({ label: user.name, value: user.id }))
                ]}
                value={selectedUser}
                onValueChange={setSelectedUser}
                placeholder="Select user"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Project</label>
              <SearchableSelect
                options={[
                  { label: 'All Projects', value: 'all' },
                  ...projects.map(project => ({ label: project.name, value: project.id }))
                ]}
                value={selectedProject}
                onValueChange={setSelectedProject}
                placeholder="Select project"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Date Range</label>
              <SearchableSelect
                options={[
                  { label: 'Today', value: 'today' },
                  { label: 'Last 24 hours', value: '1d' },
                  { label: 'Last 7 days', value: '7d' },
                  { label: 'Last 30 days', value: '30d' },
                  { label: 'Last 90 days', value: '90d' },
                  { label: 'All time', value: 'all' }
                ]}
                value={dateRange}
                onValueChange={setDateRange}
                placeholder="Select date range"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Activity Logs
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 && !loading ? (
            <div className="text-center py-8 text-muted-foreground">
              No activity logs found for the selected filters.
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-shrink-0">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={undefined} />
                      <AvatarFallback>
                        {log.user?.first_name?.[0] || log.user?.email?.[0] || (log.user_id ? log.user_id.substring(0, 1).toUpperCase() : 'S')}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {log.user_id ? getActionIcon(log.action_type) : <Settings className="w-4 h-4" />}
                      <Badge variant="secondary" className={getActionColor(log.action_type)}>
                        {log.action_type.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                      </span>
                    </div>

                    <p className="text-sm font-medium">
                      {getLogMessageWithImpersonation(log)}
                    </p>

                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <details>
                          <summary className="cursor-pointer hover:text-foreground">
                            View Details
                          </summary>
                          <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {format(new Date(log.timestamp), 'MMM dd, yyyy')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(log.timestamp), 'HH:mm:ss')}
                    </div>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 